<template>
  <div>
    <div class="is-flex is-flex-direction-row is-flex-wrap-nowrap is-align-items-center">
      <span>Erstelldatum nach dem</span>
      <b-field class="mb-0">
        <b-datepicker
          v-model="createdAfterDate"
          :show-week-number="true"
          locale="de-DE"
          placeholder="Click to select..."
          icon="calendar-today"
          class="ml-2"
          trap-focus
        />
      </b-field>
      <b-button class="is-success ml-4" :class="{ 'is-loading': csvIsLoading }" @click="runCSVExport()">
        {{ $t("exportCSVHeader") }}
      </b-button>
    </div>
    <Pagination :table-collection="translatorsCollection" sorting-field="firstName" :detailed="true">
      <template slot-scope="props" @openRemoveModal="openRemoveModal">
        <b-table-column field="firstName" :label="$t('name')" sortable>
          <span>{{ props.row.firstName + " " + props.row.lastName }}</span>
        </b-table-column>

        <b-table-column field="independent" :label="$t('independent')" centered>
          <b-checkbox type="is-success" disabled :value="props.row.independent" />
        </b-table-column>

        <b-table-column field="rating" :label="$t('rating')">
          <b-field>
            <b-tooltip type="is-primary" :label="props.row.ratingNumber + ' ' + $t('ratings')">
              <b-rate v-model="props.row.averageRating" disabled />
            </b-tooltip>
            ({{ props.row.ratingNumber }})
          </b-field>
        </b-table-column>

        <b-table-column field="approved" :label="$t('approved')" centered>
          <b-checkbox type="is-success" disabled :value="props.row.approved" />
        </b-table-column>

        <b-table-column :label="$t('actions')" numeric header-class="has-text-right">
          <span>
            <b-tooltip v-if="!props.row.approved" class="is-success" :label="$t('manualApprovalExplained')">
              <b-button
                class="mr-2"
                size="is-small"
                type="is-success"
                icon-left="check"
                :disabled="isLoading"
                :loading="isLoading"
                @click="manualApproval(props.row.id)"
              />
            </b-tooltip>
            <b-tooltip v-else class="is-warning" :label="$t('revokeApprovalTranslator')">
              <b-button
                class="mr-2"
                size="is-small"
                type="is-warning"
                icon-left="cancel"
                :disabled="isLoading"
                :loading="isLoading"
                @click="handleRevoke(props.row.id)"
              />
            </b-tooltip>
            <b-tooltip class="is-danger" :label="$t('deleteTranslator')">
              <b-button
                size="is-small"
                type="is-danger"
                icon-left="delete"
                :disabled="isLoading"
                :loading="isLoading"
                @click="handleDelete(props.row.id)"
              />
            </b-tooltip>
          </span>
        </b-table-column>
      </template>
      <template slot="detail" slot-scope="props">
        <DetailTranslatorsRow :props="props" @manual-approval="manualApproval(props.row.id)" />
      </template>
    </Pagination>
  </div>
</template>

<script>
import { functions } from "@/firebase";
import Pagination from "@/components/Pagination";
import DetailTranslatorsRow from "@/components/DetailTranslatorsRow";
export default {
  name: "TranslatorsTable",
  components: {
    Pagination,
    DetailTranslatorsRow,
  },
  props: {
    translatorsCollection: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      isLoading: false,
      csvIsLoading: false,
      createdAfterDate: null,
    };
  },
  methods: {
    async runCSVExport() {
      this.csvIsLoading = true;
      if (!this.createdAfterDate) {
        this.createdAfterDate = new Date("1970-01-01T00:00:00.000Z");
      }
      functions
        .httpsCallable("ExportRegisteredUsers")({
          type: "translators",
          date: this.createdAfterDate.getTime(),
        })
        .then((csv) => {
          // download the file
          let encodeUri = encodeURI(csv.data);
          let link = document.createElement("a");
          link.setAttribute("href", encodeUri);
          link.setAttribute("download", "translators.csv");
          document.body.appendChild(link);
          link.click();
          this.csvIsLoading = false;
        })
        .catch(() => {
          this.$buefy.toast.open({
            type: "is-danger",
            message: "Fehler beim Export",
          });
          this.csvIsLoading = false;
        });
    },
    async manualApproval(uid) {
      try {
        this.isLoading = true;
        await functions.httpsCallable("ManualApprovalTranslator")({ uid });
        this.$buefy.toast.open({
          type: "is-success",
          message: this.$t("translatorApproved"),
        });
      } catch (error) {
        this.$buefy.toast.open({
          type: "is-danger",
          message: error.toString(),
        });
      }
      this.isLoading = false;
    },
    handleDelete(uid) {
      this.$buefy.dialog.confirm({
        title: this.$t("deleteTranslator"),
        message: this.$t("deleteTranslatorQuestion"),
        confirmText: this.$t("deleteTranslator"),
        cancelText: this.$t("cancel"),
        type: "is-danger",
        closeOnConfirm: false,
        hasIcon: true,
        onConfirm: async (str, component) => {
          await this.deleteTranslator(uid);
          component.close();
          return;
        },
      });
    },
    async deleteTranslator(uid) {
      try {
        this.isLoading = true;
        await functions.httpsCallable("DeleteTranslator")({ uid });
        this.$buefy.toast.open({
          type: "is-success",
          message: this.$t("translatorDeleted"),
        });
      } catch (error) {
        this.$buefy.toast.open({
          type: "is-danger",
          message: error.toString(),
        });
      }
      this.isLoading = false;
    },
    handleRevoke(uid) {
      this.$buefy.dialog.confirm({
        title: this.$t("revokeApprovalTranslatorShort"),
        message: this.$t("revokeApprovalTranslatorQuestion"),
        confirmText: this.$t("revokeApprovalTranslatorShort"),
        cancelText: this.$t("cancel"),
        type: "is-warning",
        closeOnConfirm: false,
        hasIcon: true,
        onConfirm: async (str, component) => {
          await this.revokeApproval(uid);
          component.close();
          return;
        },
      });
    },
    async revokeApproval(uid) {
      try {
        this.isLoading = true;
        await functions.httpsCallable("RevokeApprovedTranslator")({ uid });
        this.$buefy.toast.open({
          type: "is-success",
          message: this.$t("translatorApprovelRevoked"),
        });
      } catch (error) {
        this.$buefy.toast.open({
          type: "is-danger",
          message: error.toString(),
        });
      }
      this.isLoading = false;
    },
  },
};
</script>

<style></style>
